<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('leave.edit_leave_type') }}
      </oxd-text>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="leaveType.name"
                :label="$t('general.name')"
                :rules="rules.name"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="leaveType.abbreviation"
                :label="$t('leave.leave_type_abbreviation')"
                :rules="rules.abbreviation"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="leaveType.color"
                :label="$t('leave.leave_type_background_color')"
                :rules="rules.color"
                placeholder="#AAE7A8"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="leaveType.foregroundColor"
                :label="$t('leave.leave_type_foreground_color')"
                :rules="rules.foregroundColor"
                placeholder="#14532D"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-group :classes="{wrapper: '--status-grouped-field'}">
                <template #label>
                  <div class="label-is-entitlement-situational">
                    <oxd-label
                      :label="$t('leave.is_entitlement_situational')"
                    />
                    <oxd-icon-button
                      class="--help"
                      name="exclamation-circle"
                      :with-container="false"
                      @click="onModalOpen"
                    />
                  </div>
                </template>
                <oxd-input-field
                  v-model="leaveType.situational"
                  type="radio"
                  :option-label="$t('general.yes')"
                  :value="true"
                />
                <oxd-input-field
                  v-model="leaveType.situational"
                  type="radio"
                  :option-label="$t('general.no')"
                  :value="false"
                />
              </oxd-input-group>
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="leaveType.hideLeaveBalanceOnApply"
                type="checkbox"
                :label="$t('leave.hide_leave_balance_on_apply')"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            type="button"
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
      <entitlement-situational-modal
        v-if="showModal"
        @close="onModalClose"
      ></entitlement-situational-modal>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import {
  required,
  shouldNotExceedCharLength,
} from '@ohrm/core/util/validation/rules';
import EntitlementSituationalModal from '@/orangehrmLeavePlugin/components/EntitlementSituationalModal';
import {OxdLabel} from '@ohrm/oxd';

const leaveTypeModel = {
  id: '',
  name: '',
  abbreviation: '',
  color: '',
  foregroundColor: '',
  situational: '',
  hideLeaveBalanceOnApply: false,
};

export default {
  components: {
    'oxd-label': OxdLabel,
    'entitlement-situational-modal': EntitlementSituationalModal,
  },
  props: {
    leaveTypeId: {
      type: Number,
      required: true,
    },
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/leave-types',
    );
    return {
      http,
    };
  },

  data() {
    return {
      showModal: false,
      isLoading: false,
      leaveType: {...leaveTypeModel},
      rules: {
        name: [required, shouldNotExceedCharLength(50)],
        abbreviation: [
          shouldNotExceedCharLength(10),
          (v) => {
            if (v === null || v === undefined || String(v).trim() === '') {
              return true;
            }
            return /^[\p{L}\p{N}-]+$/u.test(String(v).trim())
              ? true
              : 'Use letters, numbers, or hyphen only';
          },
        ],
        color: [
          (v) => {
            if (v === null || v === undefined || String(v).trim() === '') {
              return true;
            }
            let h = String(v).trim();
            if (h[0] !== '#') {
              h = `#${h}`;
            }
            return /^#[0-9A-Fa-f]{6}$/.test(h)
              ? true
              : 'Use # and six hex digits (e.g. #AAE7A8)';
          },
        ],
        foregroundColor: [
          (v) => {
            if (v === null || v === undefined || String(v).trim() === '') {
              return true;
            }
            let h = String(v).trim();
            if (h[0] !== '#') {
              h = `#${h}`;
            }
            return /^#[0-9A-Fa-f]{6}$/.test(h)
              ? true
              : 'Use # and six hex digits (e.g. #14532D)';
          },
          () => {
            const hasFg =
              this.leaveType.foregroundColor != null &&
              String(this.leaveType.foregroundColor).trim() !== '';
            const hasBg =
              this.leaveType.color != null &&
              String(this.leaveType.color).trim() !== '';
            if (hasFg && !hasBg) {
              return this.$t('leave.leave_type_foreground_requires_background');
            }
            return true;
          },
        ],
      },
    };
  },
  created() {
    this.isLoading = true;
    this.http
      .get(this.leaveTypeId)
      .then((response) => {
        const {data} = response.data;
        this.leaveType.id = data.id;
        this.leaveType.name = data.name;
        this.leaveType.abbreviation = data.abbreviation ?? '';
        this.leaveType.color = data.color ?? '';
        this.leaveType.foregroundColor = data.foregroundColor ?? '';
        this.leaveType.situational = data.situational;
        this.leaveType.hideLeaveBalanceOnApply = !!data.hideLeaveBalanceOnApply;

        // Fetch list data for unique test
        return this.http.getAll();
      })
      .then((response) => {
        const {data} = response.data;
        this.rules.name.push((v) => {
          const index = data.findIndex((item) => item.name == v);
          if (index > -1) {
            const {id} = data[index];
            return id != this.leaveTypeId
              ? this.$t('general.already_exists')
              : true;
          } else {
            return true;
          }
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    normalizedOptionalHexField(prop) {
      const raw = this.leaveType[prop];
      if (raw === null || raw === undefined || String(raw).trim() === '') {
        return null;
      }
      let h = String(raw).trim();
      if (h[0] !== '#') {
        h = `#${h}`;
      }
      return h.toUpperCase();
    },
    onSave() {
      this.isLoading = true;
      const color = this.normalizedOptionalHexField('color');
      const foregroundColor =
        color === null
          ? null
          : this.normalizedOptionalHexField('foregroundColor');

      this.http
        .update(this.leaveTypeId, {
          name: this.leaveType.name,
          situational: this.leaveType.situational,
          hideLeaveBalanceOnApply: this.leaveType.hideLeaveBalanceOnApply,
          abbreviation:
            this.leaveType.abbreviation &&
            String(this.leaveType.abbreviation).trim() !== ''
              ? String(this.leaveType.abbreviation).trim()
              : null,
          color,
          foregroundColor,
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.onCancel();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onCancel() {
      navigate('/leave/leaveTypeList');
    },
    onModalOpen() {
      this.showModal = true;
    },
    onModalClose() {
      this.showModal = false;
    },
  },
};
</script>

<style src="./leave-type.scss" lang="scss" scoped></style>
